/**
 * CSV builders for the economic-outlook download button (superuser-only).
 *
 * Each builder fetches the relevant series, turns it into CSV text with a
 * trailing comment block, and returns the text plus a filename. The page
 * component just triggers the download — the row-shaping lives here.
 */
import { apiGet } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';
import { csvNum } from '@/lib/csv';
import type {
  CountryIso,
  InflationDataResponse,
  InflationType,
  NowcastDataResponse,
  Timeframe,
} from '../types';

/** CSV text + the filename to save it under. `null` means "no data to download". */
export interface CsvFile {
  text: string;
  filename: string;
}

function todayStamp(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Shared trailing comment block appended to every export. */
function commentLines(headerComment: string, timeframe: Timeframe, unit: string): string[] {
  return [
    '',
    headerComment,
    `# Timeframe: ${timeframe}`,
    `# Downloaded: ${new Date().toISOString()}`,
    '# Source: Fulcrum Asset Management',
    unit,
  ];
}

/** Fetch + format the inflation series as a CSV file. */
export async function buildInflationCsv(
  country: CountryIso,
  inflationType: InflationType,
  timeframe: Timeframe,
): Promise<CsvFile | null> {
  const result = await apiGet<InflationDataResponse>(
    endpoints.forecasts.inflation({ country, type: inflationType, timeframe }),
  );
  const data = result.data ?? [];
  if (data.length === 0) return null;

  const lines: string[] = [
    'Date,Actual,Median (p50),Lower Inner (p16),Upper Inner (p84),Lower Outer (p5),Upper Outer (p95),One Month Ago,Central Bank Forecast',
  ];
  for (const r of data) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const row = r as any;
    lines.push(
      [
        row.obs_date ?? row.date ?? '',
        csvNum(row.actual),
        csvNum(row.p50),
        csvNum(row.p16),
        csvNum(row.p84),
        csvNum(row.p5),
        csvNum(row.p95),
        csvNum(row['one month ago']),
        csvNum(row.cb),
      ].join(','),
    );
  }
  lines.push(
    ...commentLines(
      `# Inflation Forecast Data for ${country} - ${inflationType}`,
      timeframe,
      '# All values in % YoY',
    ),
  );
  return {
    text: lines.join('\n'),
    filename: `inflation_${country}_${inflationType}_${timeframe}_${todayStamp()}.csv`,
  };
}

/** Fetch + format the nowcast (GDP) series as a CSV file. */
export async function buildNowcastCsv(
  country: CountryIso,
  timeframe: Timeframe,
): Promise<CsvFile | null> {
  const result = await apiGet<NowcastDataResponse>(
    endpoints.forecasts.nowcast({ country, timeframe }),
  );
  const data = result.data ?? [];
  if (data.length === 0) return null;

  const lines: string[] = [
    'Date,Median (p50),Lower Inner (p16),Upper Inner (p84),Lower Outer (p5),Upper Outer (p95),Long-Run Trend',
  ];
  for (const r of data) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const row = r as any;
    lines.push(
      [
        row.obs_date ?? row.date ?? '',
        csvNum(row.p50),
        csvNum(row.p16),
        csvNum(row.p84),
        csvNum(row.p5),
        csvNum(row.p95),
        csvNum(row.trend),
      ].join(','),
    );
  }
  lines.push(
    ...commentLines(`# Nowcast Data for ${country}`, timeframe, '# All values in % Ann.'),
  );
  return {
    text: lines.join('\n'),
    filename: `nowcast_${country}_${timeframe}_${todayStamp()}.csv`,
  };
}
